import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { Send, Sparkles, Lock } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const mdComponents = {
  a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
};
import Shell from "@/components/Shell";
import { BACKEND_URL, makeApi, previewHeaders } from "@/lib/api";

export default function Chat() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [ai, setAi] = useState(null);
  const scrollRef = useRef(null);

  const loadStatus = () => api.get("/ai/status").then(({ data }) => setAi(data)).catch(() => {});
  const locked = ai && !ai.unlimited && ai.remaining === 0;

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/chat/history");
        setMessages(data.messages || []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || sending || locked) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setSending(true);
    try {
      const token = await getToken();
      const resp = await fetch(`${BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...previewHeaders() },
        body: JSON.stringify({ message: text }),
      });
      if (resp.status === 402) {
        setMessages((m) => m.slice(0, -2));
        setInput(text);
        setAi((a) => ({ ...a, remaining: 0 }));
        return;
      }
      if (!resp.ok) throw new Error("chat failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, "");
          if (!line) continue;
          try {
            const obj = JSON.parse(line);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, there was an error. Try again." };
        return copy;
      });
    } finally {
      setSending(false);
      loadStatus();
    }
  };

  const suggestions = [
    "What's the difference between MC Authority and USDOT?",
    "Do I need BMC-91 if I only haul intrastate in Texas?",
    "How long does ELD enrollment take?",
    "Which insurance is required to start interstate operations?",
  ];

  return (
    <Shell>
      <div className="mb-6">
        <div className="font-mono-label text-xs text-[#FFD600] mb-2">COMPLIANCE ASSISTANT</div>
        <h1 className="font-heading font-black text-4xl leading-none" data-testid="chat-title">Ask anything about FMCSA.</h1>
      </div>

      <div className="grid lg:grid-cols-4 gap-6 min-w-0 [&>*]:min-w-0">
        <aside className="lg:col-span-1 lg:brutal-border lg:bg-[#141414] lg:p-5 h-fit min-w-0">
          <div className="font-mono-label text-[10px] text-muted-foreground mb-3 lg:mb-4">TRY ASKING</div>
          <div className="flex lg:flex-col gap-2 overflow-x-auto pb-1 snap-x">
            {suggestions.map((s, i) => (
              <button
                key={i}
                data-testid={`suggestion-${i}`}
                onClick={() => setInput(s)}
                className="text-xs text-left shrink-0 w-[240px] lg:w-full min-h-[44px] p-3 border border-[#262626] bg-[#141414] lg:bg-transparent hover:border-white transition-colors snap-start"
              >
                {s}
              </button>
            ))}
          </div>
        </aside>

        <div className="lg:col-span-3 brutal-border bg-[#0A0A0A] flex flex-col h-[calc(100dvh-330px)] min-h-[380px] lg:h-[70vh]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                <Sparkles size={32} className="mb-3 text-[#FFD600]" />
                <div className="font-heading font-black text-xl">Ready when you are.</div>
                <div className="text-sm">Powered by Claude Sonnet 5.5.</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`message-${m.role}-${i}`}
                  className={`max-w-[80%] p-4 text-sm ${m.role === "user" ? "bg-[#0055FF] text-white whitespace-pre-wrap" : "bg-[#141414] border border-[#262626] ai-md"}`}
                >
                  {!m.content ? <span className="opacity-60">…</span>
                    : m.role === "user" ? m.content
                    : <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{m.content}</ReactMarkdown>}
                </div>
              </div>
            ))}
          </div>
          {ai && !ai.unlimited && !locked && (
            <div className="border-t border-[#262626] px-4 py-2 text-xs text-muted-foreground flex justify-between" data-testid="ai-free-remaining">
              <span>{ai.remaining} of {ai.limit} free questions left</span>
              <Link to="/pricing" className="underline hover:text-white" data-testid="ai-upgrade-link">Get unlimited</Link>
            </div>
          )}
          {locked ? (
            <div className="border-t border-[#262626] p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-[#141414]" data-testid="ai-upgrade-card">
              <div className="flex items-start gap-3">
                <Lock size={18} className="text-[#FFD600] mt-1 shrink-0" />
                <div>
                  <div className="font-heading font-bold">You've used your {ai.limit} free questions.</div>
                  <div className="text-sm text-muted-foreground">Choose any plan to keep asking the AI assistant without limits.</div>
                </div>
              </div>
              <Link to="/pricing" data-testid="ai-upgrade-button" className="cta-primary px-5 py-3 font-mono-label text-xs text-center shrink-0">See plans</Link>
            </div>
          ) : (
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-[#262626] p-4 flex gap-3"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about USDOT, BMC-91, ELD rules…"
              className="flex-1 min-w-0 bg-[#141414] border border-[#262626] px-4 py-3 text-base sm:text-sm focus:outline-none focus:border-white"
            />
            <button data-testid="chat-send" type="submit" disabled={sending} className="cta-primary px-5 py-3 font-mono-label text-xs inline-flex items-center gap-2 disabled:opacity-50">
              <Send size={14} /> Send
            </button>
          </form>
          )}
        </div>
      </div>
    </Shell>
  );
}
